/**
 * Fiscal-period labelling. GRM's fiscal year runs 1 March → end of February, so fiscal
 * Q1 is Mar–May, Q2 Jun–Aug, Q3 Sep–Nov and Q4 Dec–Feb.
 *
 * EVERY FUNCTION HERE DERIVES ITS ANSWER BY SPLITTING THE STRING, never by building a
 * Date. `new Date('2026-03')` is parsed as midnight UTC, so in any negative offset it
 * renders as February — the bug that shifted every key in the Sales Forecast's
 * year-on-year overlay and made it report 28% growth where the real figure was 22%. The
 * tests stub `getTimezoneOffset` to prove these are immune to it.
 *
 * These moved out of RevenueTrendReport.tsx when the forecast needed them too. Note there
 * are still separate copies of the fiscal-YEAR *range* rule in lib/utils.ts and
 * pages/Dashboard.tsx (the latter with hardcoded, non-leap-safe quarter bounds); this
 * module covers labelling only and does not attempt to unify those.
 */

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

/** The fiscal year runs March–February, so index 1 is March and index 12 is February. */
const FISCAL_MONTH_ABBR = [
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
  'Jan',
  'Feb',
] as const

/** `'2026-03'` → `'Mar 2026'`. Malformed input is returned unchanged, never `'undefined'`. */
export const monthLabel = (key: string): string => {
  const [year, month] = key.split('-')
  const name = MONTH_ABBR[Number(month) - 1]
  return name ? `${name} ${year}` : key
}

/** `'2026-03'` → `'Mar 26'`, for a crowded category axis. */
export const shortMonthLabel = (key: string): string => {
  const [year, month] = key.split('-')
  const name = MONTH_ABBR[Number(month) - 1]
  return name ? `${name} ${(year ?? '').slice(2)}` : key
}

/** Fiscal month index (1 = March) → `'Mar'`. */
export const fiscalLabel = (index: number): string => FISCAL_MONTH_ABBR[index - 1] ?? String(index)

/** Fiscal year start year → `'FY2026/27'`. */
export const fiscalYearLabel = (fy: number): string => `FY${fy}/${String(fy + 1).slice(2)}`

/**
 * The fiscal quarter key for a `'YYYY-MM'` month key: `'2026-01'` → `'2025-Q4'`.
 *
 * Mirrors the SQL the forecast endpoint uses, so a client-side bucket agrees with a
 * server-side one. January and February belong to the fiscal year that began the
 * previous March, which is the case naive quarter logic gets wrong.
 */
export const fiscalQuarterKeyOf = (monthKey: string): string => {
  const parts = monthKey.slice(0, 7).split('-')
  const year = Number(parts[0])
  const month = Number(parts[1])
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return monthKey
  }
  // January and February belong to the fiscal year that began the previous March.
  const fiscalYear = month >= 3 ? year : year - 1
  const fiscalMonthIndex = ((month + 9) % 12) + 1
  return `${fiscalYear}-Q${Math.ceil(fiscalMonthIndex / 3)}`
}

/** `'2026-Q1'` → `'Q1 FY2026/27'`. Anything else is returned unchanged. */
export const fiscalQuarterLabel = (periodKey: string): string => {
  const match = /^(\d{4})-Q([1-4])$/.exec(periodKey)
  if (!match) return periodKey
  return `Q${match[2]} ${fiscalYearLabel(Number(match[1]))}`
}

/** `'2026-Q1'` → `'Q1 FY27'`, for a crowded category axis. */
export const shortFiscalQuarterLabel = (periodKey: string): string => {
  const match = /^(\d{4})-Q([1-4])$/.exec(periodKey)
  if (!match) return periodKey
  return `Q${match[2]} FY${String(Number(match[1]) + 1).slice(2)}`
}

/** The months a fiscal quarter covers, e.g. `'2025-Q4'` → `'Dec – Feb'`. */
export const fiscalQuarterMonths = (periodKey: string): string => {
  const match = /^(\d{4})-Q([1-4])$/.exec(periodKey)
  if (!match) return ''
  const first = (Number(match[2]) - 1) * 3
  return `${FISCAL_MONTH_ABBR[first]} – ${FISCAL_MONTH_ABBR[first + 2]}`
}

export type Grain = 'month' | 'quarter'

/**
 * Label a period key according to the grain, so callers do not branch on it themselves.
 * `compact` is for category axes, where the full label does not fit.
 */
export const periodLabel = (periodKey: string, grain: Grain, compact = false): string => {
  if (grain === 'quarter') {
    return compact ? shortFiscalQuarterLabel(periodKey) : fiscalQuarterLabel(periodKey)
  }
  return compact ? shortMonthLabel(periodKey) : monthLabel(periodKey)
}

/**
 * The lookback and horizon choices for each grain.
 *
 * The request always travels in MONTHS — the grain is a display concern — so these are
 * month values wearing quarter labels. Quarterly lookbacks start at 12 months because
 * a 6-month lookback is two quarters, far too thin a base to read a trend from.
 */
export const LOOKBACK_OPTIONS: Record<Grain, Array<{ months: number; label: string }>> = {
  month: [
    { months: 6, label: '6 mo' },
    { months: 12, label: '12 mo' },
    { months: 24, label: '24 mo' },
  ],
  quarter: [
    { months: 12, label: '4 qtr' },
    { months: 24, label: '8 qtr' },
    { months: 36, label: '12 qtr' },
  ],
}

export const HORIZON_OPTIONS: Record<Grain, Array<{ months: number; label: string }>> = {
  month: [
    { months: 3, label: '3 mo' },
    { months: 6, label: '6 mo' },
    { months: 12, label: '12 mo' },
  ],
  quarter: [
    { months: 3, label: '1 qtr' },
    { months: 6, label: '2 qtr' },
    { months: 12, label: '4 qtr' },
  ],
}

/**
 * Keep a month value valid when the grain changes.
 *
 * Flipping to quarterly from a 6-month lookback would otherwise leave the Select holding
 * a value absent from its own option list — which Radix renders as the placeholder, so
 * the control silently appears empty. Snaps to the nearest offered value instead.
 */
export const remapPeriodChoice = (months: number, options: Array<{ months: number }>): number => {
  if (options.some((o) => o.months === months)) return months
  return options.reduce((best, o) =>
    Math.abs(o.months - months) < Math.abs(best.months - months) ? o : best
  ).months
}

/** "3 months" / "1 quarter", for captions that quote the horizon back to the reader. */
export const describeSpan = (months: number, grain: Grain): string => {
  if (grain === 'quarter') {
    const quarters = Math.max(1, Math.round(months / 3))
    return `${quarters} ${quarters === 1 ? 'quarter' : 'quarters'}`
  }
  return `${months} ${months === 1 ? 'month' : 'months'}`
}

/** "month" / "quarter" — the singular noun, for headline labels. */
export const periodNoun = (grain: Grain): string => (grain === 'quarter' ? 'quarter' : 'month')
